import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, X, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

export default function AiChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const bodyRef = useRef();

  // Load persisted thread from server whenever the panel is first opened for this user.
  useEffect(() => {
    if (!user || !open || messages.length > 0) return;
    let alive = true;
    setLoadingHistory(true);
    api.get("/ai/chat/history")
      .then((r) => { if (alive) setMessages(r.data?.messages || []); })
      .catch(() => { /* silent — first open, nothing to show */ })
      .finally(() => { if (alive) setLoadingHistory(false); });
    return () => { alive = false; };
  }, [user, open, messages.length]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open, sending]);

  if (!user) return null;

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    const nextHistory = [...messages, { role: "user", content: msg }];
    setMessages(nextHistory);
    setSending(true);
    try {
      const r = await api.post("/ai/chat", { message: msg });
      setMessages([...nextHistory, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chat failed");
      setMessages([...nextHistory, { role: "assistant", content: "_Sorry — I couldn't respond right now. Please try again._" }]);
    } finally { setSending(false); }
  };

  const clearThread = async () => {
    if (!window.confirm("Clear the entire chat thread? This cannot be undone.")) return;
    try {
      await api.delete("/ai/chat/history");
      setMessages([]);
      toast.success("Chat thread cleared");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear thread");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-[#0F172A] text-white shadow-lg hover:bg-slate-800 flex items-center justify-center"
        data-testid="ai-chat-fab"
        title="Ask My RKF"
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed bottom-20 right-5 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] bg-white border border-slate-200 rounded-lg shadow-2xl flex flex-col" data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <div>
                <div className="font-semibold text-sm">Ask My RKF</div>
                <div className="text-[10px] text-slate-500">Powered by Claude Sonnet 5 · Synced</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={clearThread} title="Clear thread" data-testid="ai-chat-clear">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setOpen(false)} data-testid="ai-chat-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="ai-chat-body">
            {loadingHistory && messages.length === 0 ? (
              <div className="text-center text-slate-400 text-xs mt-8">Loading your chat history…</div>
            ) : messages.length === 0 ? (
              <div className="text-center text-slate-500 text-xs mt-8 space-y-2">
                <div className="text-slate-700 font-medium">Ask anything about grants, budgets or UCs.</div>
                <div>Try:</div>
                <ul className="text-slate-500 text-xs space-y-1">
                  <li>"How do I write a good Schedule VII narration?"</li>
                  <li>"What TDS rate applies to professional fees?"</li>
                  <li>"Explain ICAI UC Annexure A row 3."</li>
                </ul>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-800"}`} data-testid={`ai-chat-msg-${m.role}-${i}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-3 py-2 bg-slate-100 text-slate-500 text-xs italic">Thinking…</div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about budgets, UCs, TDS…"
              className="flex-1 text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={sending}
              data-testid="ai-chat-input"
            />
            <Button type="submit" size="sm" disabled={sending || !input.trim()} className="bg-[#0F172A]" data-testid="ai-chat-send">
              <Send className="w-4 h-4 mr-1" /> Send
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
